export const EFilterType = {
  PartialMatch: 0,
  ExactValue: 1,
  Range: 2
} as const;

export const UNASSIGNED_GROUP_SENTINEL = "__unassigned__";

export interface IObjectsOverviewFilters {
  objectTypeId: string;
  groupId: string;
  lastCommunicationStart: string | null;
  lastCommunicationEnd: string | null;
  dynamicFieldId: string;
  dynamicFieldValue: string;
}

export interface IWhereClause {
  field: string;
  value: string | boolean | number;
  type: number;
  value2?: string;
}

export const objectsOverviewFiltersInitialValues: IObjectsOverviewFilters = {
  objectTypeId: "",
  groupId: "",
  lastCommunicationStart: null,
  lastCommunicationEnd: null,
  dynamicFieldId: "",
  dynamicFieldValue: ""
};

/** Normalize a picked calendar day to local start-of-day ISO for range queries. */
export const toFilterRangeStartIso = (date: Date | string): string => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized.toISOString();
};

/** Normalize a picked calendar day to local end-of-day ISO so same-day hits are included. */
export const toFilterRangeEndIso = (date: Date | string): string => {
  const normalized = new Date(date);
  normalized.setHours(23, 59, 59, 999);
  return normalized.toISOString();
};

/** Escape a string embedded in a Dynamic LINQ expression fragment. */
export const escapeDynamicLinqString = (value: string): string =>
  value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');

/**
 * Build a predicate that matches a specific metadata field + value on the same row.
 * Sent as ExactValue/true so PagingExtensions keeps the expression as a boolean equality.
 */
export const buildDynamicFieldPredicate = (
  fieldUniqueId: string,
  fieldValue: string
): string => {
  const id = escapeDynamicLinqString(fieldUniqueId.trim());
  const value = escapeDynamicLinqString(fieldValue.trim());
  return `Metadata.Any(Field != null && Field.UniqueId.ToString().ToLower() == "${id.toLowerCase()}" && Value != null && Value.ToLower().Contains("${value.toLowerCase()}"))`;
};

export const countActiveObjectFilters = (
  filters: IObjectsOverviewFilters
): number => {
  let count = 0;
  if (filters.objectTypeId) count += 1;
  if (filters.groupId) count += 1;
  if (filters.lastCommunicationStart && filters.lastCommunicationEnd)
    count += 1;
  if (filters.dynamicFieldId && filters.dynamicFieldValue.trim()) count += 1;
  return count;
};

export const buildObjectsFilterWhereClauses = (
  filters: IObjectsOverviewFilters
): IWhereClause[] => {
  const where: IWhereClause[] = [];

  if (filters.objectTypeId) {
    where.push({
      field: "ObjectType.UniqueId",
      value: filters.objectTypeId,
      type: EFilterType.ExactValue
    });
  }

  if (filters.groupId === UNASSIGNED_GROUP_SENTINEL) {
    where.push({
      field: "Groups.Count",
      value: 0,
      type: EFilterType.ExactValue
    });
  } else if (filters.groupId) {
    where.push({
      field: "Groups.UniqueId",
      value: filters.groupId,
      type: EFilterType.ExactValue
    });
  }

  if (filters.lastCommunicationStart && filters.lastCommunicationEnd) {
    where.push({
      field: "LastUpdated",
      value: toFilterRangeStartIso(filters.lastCommunicationStart),
      value2: toFilterRangeEndIso(filters.lastCommunicationEnd),
      type: EFilterType.Range
    });
  }

  if (filters.dynamicFieldId && filters.dynamicFieldValue.trim()) {
    where.push({
      field: buildDynamicFieldPredicate(
        filters.dynamicFieldId,
        filters.dynamicFieldValue
      ),
      value: true,
      type: EFilterType.ExactValue
    });
  }

  return where;
};

const FILTER_URL_KEYS = {
  objectTypeId: "filterObjectType",
  groupId: "filterGroup",
  lastCommunicationStart: "filterLastCommStart",
  lastCommunicationEnd: "filterLastCommEnd",
  dynamicFieldId: "filterDynamicField",
  dynamicFieldValue: "filterDynamicValue"
} as const;

export const parseObjectsFiltersFromSearchParams = (
  searchParams: URLSearchParams
): IObjectsOverviewFilters => {
  return {
    objectTypeId: searchParams.get(FILTER_URL_KEYS.objectTypeId) || "",
    groupId: searchParams.get(FILTER_URL_KEYS.groupId) || "",
    lastCommunicationStart:
      searchParams.get(FILTER_URL_KEYS.lastCommunicationStart) || null,
    lastCommunicationEnd:
      searchParams.get(FILTER_URL_KEYS.lastCommunicationEnd) || null,
    dynamicFieldId: searchParams.get(FILTER_URL_KEYS.dynamicFieldId) || "",
    dynamicFieldValue: searchParams.get(FILTER_URL_KEYS.dynamicFieldValue) || ""
  };
};

export const applyObjectsFiltersToSearchParams = (
  params: URLSearchParams,
  filters: IObjectsOverviewFilters
): void => {
  // Drop legacy overview Status URL param; inactive visibility uses the shared toggle.
  params.delete("filterStatus");

  const entries: Array<[string, string | null | undefined]> = [
    [FILTER_URL_KEYS.objectTypeId, filters.objectTypeId || null],
    [FILTER_URL_KEYS.groupId, filters.groupId || null],
    [
      FILTER_URL_KEYS.lastCommunicationStart,
      filters.lastCommunicationStart || null
    ],
    [
      FILTER_URL_KEYS.lastCommunicationEnd,
      filters.lastCommunicationEnd || null
    ],
    [FILTER_URL_KEYS.dynamicFieldId, filters.dynamicFieldId || null],
    [FILTER_URL_KEYS.dynamicFieldValue, filters.dynamicFieldValue || null]
  ];

  entries.forEach(([key, value]) => {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  });
};
